
















































import Vue from 'vue';
import moment from 'moment';
import { IMutation, IShoppingCartIndividual, IShoppingCartItem } from '@/common/interfaces/cart.interface';
import { IIcons } from '@/common/interfaces/index.interface';
import { ILimitingObject, ILimitingPosition } from '@/common/interfaces/limiting.interface';
import { IProduct, IProductCategory } from '@/common/interfaces/products.interface';
import { TDisplayMode } from '@/common/types/index.types';
import config from '../../../config';
import OldCartHelper from '../../helpers/OldCartHelper';
import CookieHelper from '../../helpers/CookieHelper';
import CustomerHelper from '../../helpers/CustomerHelper';
import ErrorHandlerHelper from '../../helpers/ErrorHandlerHelper';
import FetchHelper from '../../helpers/OldFetchHelper';
import EventListenerHelper from '../../helpers/EventListenerHelper';
import URLHelper from '../../helpers/URLHelper';
import TIcons from '../../types/TIcons';
import MerchandiseOverlay from '../common/merchandise-overlay/MerchandiseOverlay.vue';
import MerchandiseOverlayMethods from '../common/merchandise-overlay/MerchandiseOverlayMethods';
import LoadingSpinner from '../common/loading/LoadingSpinner.vue';
import ProductList from '../productlist/ProductList.vue';
import ProductsOverviewCategories from './ProductsOverviewCategories.vue';
import ProductsOverviewHead from './ProductsOverviewHead.vue';
import ProductsOverviewHeader from './ProductsOverviewHeader.vue';

interface IProductsOverview {
  availableCarts: IShoppingCartIndividual[];
  displayMode: TDisplayMode;
  eventNotRunning: boolean;
  hasMultipleCarts: boolean;
  icons: IIcons;
  initialized: boolean;
  isEventPage: boolean;
  isPartner: boolean;
  limiting: ILimitingObject;
  merchandiseOverlay: {
    isBusinessCard: boolean;
    isCarDecal: boolean;
    item: (IShoppingCartItem & { tempMutations: IMutation[] }) | null;
    loading: boolean;
    visible: boolean;
  };
  moment: typeof moment;
  nextReload: number;
  productCategories: IProductCategory[];
  products: IProduct[];
  selectedCartId: number | null;
  showCategories: boolean;
}

export default Vue.extend({
  name: `ProductsOverview`,
  components: {
    LoadingSpinner,
    MerchandiseOverlay,
    ProductList,
    ProductsOverviewCategories,
    ProductsOverviewHead,
    ProductsOverviewHeader,
  },
  data(): IProductsOverview {
    return {
      availableCarts: [],
      displayMode: `grid`,
      eventNotRunning: false,
      hasMultipleCarts: false,
      icons: TIcons,
      initialized: false,
      isEventPage: false,
      isPartner: false,
      limiting: {
        active: false,
        documentEventListeners: [],
        position: {
          current: -1,
          list: `ll`,
          total: -1,
        },
        shoppingAllowed: true,
      },
      merchandiseOverlay: {
        isBusinessCard: false,
        isCarDecal: false,
        item: null,
        loading: false,
        visible: false,
      },
      moment,
      nextReload: -1,
      productCategories: [],
      products: [],
      selectedCartId: null,
      showCategories: false,
    };
  },
  created(): void {
    this.limiting.documentEventListeners = [
      { callback: this.updateLimitingActive, name: `rin-limiting-active` },
      { callback: this.updateLimitingActive, name: `rin-limiting-still-active` },
      { callback: this.updateLimitingNotActive, name: `rin-limiting-not-active` },
      { callback: this.updateLimitingPosition, name: `rin-limiting-position-update` },
      { callback: this.updateLimitingNotActive, name: `rin-limiting-request-with-deactivated-bouncer` },
      { callback: this.updateShoppingAllowed, name: `rin-limiting-shopping-allowed` },
      { callback: this.updateShoppingDisallowed, name: `rin-limiting-shopping-disallowed` },
    ];
  },
  async mounted(): Promise<void> {
    EventListenerHelper.setUpEventListeners(this.limiting.documentEventListeners);
    await this.setUp(true);
  },
  beforeDestroy(): void {
    EventListenerHelper.teardownEventListeners(this.limiting.documentEventListeners);
  },
  methods: {
    ...MerchandiseOverlayMethods,
    changeDisplayMode(mode: TDisplayMode): void {
      this.displayMode = mode;
    },
    async getCategories(): Promise<void> {
      let category;
      const { slug } = URLHelper.parsedUrlObject;
      if(config.products.shopCategories.complete.includes(slug)) {
        category = `complete`;
      } else if(config.products.shopCategories.fresh.includes(slug)) {
        category = `fresh`;
      } else if(config.products.shopCategories.sports.includes(slug)) {
        category = `sports`;
      } else if(config.products.shopCategories.supplements.includes(slug)) {
        category = `supplements`;
      } else {
        return; // we are somewhere else, don't grab categories, we don't need them
      }

      const fetchConfig = {
        baseURL: config.wpUrls.restapi.path,
        path: `${config.wpUrls.restapi.endpoints.productCategories}?category=${category}`,
      };

      const fetchResult = await FetchHelper.get(fetchConfig);
      if(Array.isArray(fetchResult) && fetchResult.length > 0) {
        this.productCategories = fetchResult;
        this.productCategories.unshift({
          count: fetchResult.reduce((a, b) => a + b.count, 0),
          isParentCategory: true,
          link: `/${category}`,
          name: category,
        });
      }
    },
    async getEventProducts(): Promise<void> {
      let eventStart: number | string = 0;
      let eventEnd: number | string = 0;
      this.nextReload = 86400;
      const { slug } = URLHelper.parsedUrlObject;
      const slugToUse = slug.split(`/`)[1] || slug; // we only need the part after the eventproductsoverview/
      const getPartnerProducts = config.products.partnerShopSlugs.includes(slugToUse);
      const fetchConfig = {
        baseURL: config.wpUrls.restapi.path,
        path: `${config.wpUrls.restapi.endpoints.eventProductsOverviewBySlug}?slug=${slugToUse}&partnerProducts=${getPartnerProducts}`,
      };
      const fetchResult = await FetchHelper.get(fetchConfig);
      if(fetchResult && Array.isArray(fetchResult)) {
        const foundProducts = [];
        for(const event of fetchResult) {
          eventStart = new Date(event.eventStartTime).toUTCString();
          eventEnd = new Date(event.eventEndTime).toUTCString();
          if(moment().isBefore(eventStart)) {
            this.nextReload = moment(eventStart).unix() - moment().unix();
          }
          if(event.eventProducts && Array.isArray(event.eventProducts)) {
            const productsAllowedToBeShown = [];
            for(const product of event.eventProducts) {
              const endTime = new Date(product.productEndTime).toUTCString();
              const startTime = new Date(product.productStartTime).toUTCString();
              const allowedToBeShown = moment().utc().isAfter(startTime)
                && moment().utc().isBefore(endTime);
              if(allowedToBeShown) {
                productsAllowedToBeShown.push(product);
                const durationEnd = moment(endTime).unix() - moment().unix();
                this.nextReload = (durationEnd >= 0 && durationEnd < this.nextReload)
                  ? durationEnd
                  : this.nextReload;
              } else {
                const durationStart = moment(startTime).unix() - moment().unix();
                this.nextReload = (durationStart >= 0 && durationStart < this.nextReload)
                  ? durationStart
                  : this.nextReload;
              }
            }
            foundProducts.push(...productsAllowedToBeShown);
          }
        }
        this.products = foundProducts;
      }
      this.eventNotRunning = moment().isBefore(eventStart) || moment().isAfter(eventEnd);
      if(this.nextReload >= 0) {
        setTimeout(() => {
          window.location.reload();
        }, this.nextReload * 1000);
      }
    },
    async getProductDataFromAPI(): Promise<void> {
      if(this.products.length <= 0) {
        return;
      }
      const matchcodes = this.products.map(product => product.matchcode);
      const matchcodesQuery = matchcodes.length > 0 ? `&matchcodes[]=${matchcodes.join(`&matchcodes[]=`)}` : ``;

      const fetchConfig = {
        path: `${config.api.paths.products.basepath}/?currency=${CookieHelper.getCurrency()}${matchcodesQuery}&language=${CookieHelper.getLanguage()}`,
      };
      const fetchResult = await FetchHelper.get(fetchConfig);
      if(!fetchResult.success) {
        ErrorHandlerHelper.handleError(fetchResult, { logToConsole: true, showSwal: true });
        return;
      }

      this.products = (fetchResult.data as IProduct[]).map(product => {
        const index = this.products.findIndex(p => product.matchcode === p.matchcode);
        if(this.isEventPage) {
          this.products[index].image += `?q=100`;
          this.products[index] = { ...product, ...this.products[index] };
        } else {
          this.products[index] = { ...this.products[index], ...product };
        }
        return this.products[index];
      });
    },
    async getProducts(): Promise<void> {
      const { slug } = URLHelper.parsedUrlObject;
      let slugToUse;
      if(config.products.shopCategories.offers.includes(slug)) {
        slugToUse = `offers`;
      } else {
        slugToUse = slug.split(`/`)[1] || slug;
      }

      if(slugToUse === `partner-shop` && !this.isPartner) {
        window.location.href = `/`;
      } else {
        const getPartnerProducts = config.products.partnerShopSlugs.includes(slugToUse);
        const fetchConfig = {
          baseURL: config.wpUrls.restapi.path,
          path: `${config.wpUrls.restapi.endpoints.productsByCategory}?category=${slugToUse}&partnerProducts=${getPartnerProducts}`,
        };
        const fetchResult = await FetchHelper.get(fetchConfig);
        if(Array.isArray(fetchResult) && fetchResult.length > 0) {
          this.products = fetchResult;
        }
      }
    },
    async isPartnerPreparation(initialLoad: boolean): Promise<void> {
      if(initialLoad && window.outerWidth >= config.breakpoints.gridToListView) {
        this.displayMode = `list`;
      }

      this.hasMultipleCarts = await OldCartHelper.hasMultipleCarts();
      const lastUsedCartId = CookieHelper.getLastUsedCartId();
      if(this.hasMultipleCarts && lastUsedCartId) {
        this.selectedCartId = lastUsedCartId;
      }
    },
    async prepareCarts(): Promise<void> {
      this.availableCarts = await OldCartHelper.getCarts();
      this.selectedCartId = this.availableCarts[0]?.cartId ?? null;

      const eventListener = async (): Promise<void> => {
        document.removeEventListener(`ccn-set-rin-session`, eventListener);
        this.availableCarts = await OldCartHelper.getCarts();
        this.selectedCartId = this.availableCarts[0]?.cartId ?? null;
      };
      document.addEventListener(`ccn-set-rin-session`, eventListener);
    },
    async setUp(initialLoad = false): Promise<void> {
      this.isPartner = await CustomerHelper.getPartnerStatus();
      this.isEventPage = URLHelper.parsedUrlObject.slug.includes(`eventproductoverview`);
      if(this.isEventPage) {
        await this.getEventProducts();
      } else {
        await Promise.all([
          this.getCategories(),
          this.getProducts(),
        ]);
      }
      await this.getProductDataFromAPI();

      await this.prepareCarts();

      if(this.isPartner) {
        await this.isPartnerPreparation(initialLoad);
      }
      this.initialized = true;
    },
    toggleCategoryVisibility(): void {
      this.showCategories = !this.showCategories;
    },
    async updateBusinessCard(): Promise<void> {
      if(!this.areAllRequiredFieldsFilledOut()) {
        return;
      }
      this.merchandiseOverlay.loading = true;
      await this.setMerchandiseOverlayMutations();
      const { item } = this.merchandiseOverlay;
      if(!item) {
        return;
      }
      const body: { amount: number; matchcode: string; [index: string]: number | string } = { amount: item.amount, matchcode: item.matchcode };

      if(this.merchandiseOverlay.item) {
        for(const mutation of Object.keys(this.merchandiseOverlay.item.mutations)) {
          body[`mutation${mutation}`] = this.merchandiseOverlay.item.mutations[+mutation].value;
        }
      }

      try {
        await OldCartHelper.updateItem(body, item.cartContentId);
        this.$swal(``, this.$t(`products-overview:editSuccess`), `success`);
      } catch (err) {
        ErrorHandlerHelper.handleError(err, { showSwal: true });
      }

      this.closeMerchandiseOverlay(false);
    },
    updateLimitingActive(): void {
      this.limiting.active = true;
    },
    updateLimitingNotActive(): void {
      this.limiting.active = false;
    },
    updateLimitingPosition(event: { detail: ILimitingPosition }): void {
      this.limiting.position = event.detail;
    },
    updateSelectedCartId(cartId: number): void {
      this.selectedCartId = cartId;
    },
    async updateShoppingAllowed(): Promise<void> {
      await this.setUp();
      this.limiting.shoppingAllowed = true;
    },
    updateShoppingDisallowed(): void {
      this.limiting.shoppingAllowed = false;
    },
  },
});
